import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, Button, Card, Row, Col, InputNumber, Select, Tabs, Table, Tag, Space, Popconfirm, Modal, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { customBaseUrl, httpPost, httpGet, httpPut, httpDelete } from '../../store/http';
import { hideLoader, showLoader } from '../../utils/loader';
import { NotificationManager } from 'react-notifications';
import Layout from "../../components/Layout";

const { Option } = Select;
const { TabPane } = Tabs;

const ReminderConfigs = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [configForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);


  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await httpGet(`${customBaseUrl.loanUrl}/api/v1/admin/reminder-configs`, null, true);
      const data = res?.data || res || [];
      if (Array.isArray(data)) {
        setConfigs(data);
      } else {
        setConfigs([]);
      }
    } catch (error) {
      console.error('Error fetching reminder configs:', error);
      NotificationManager.error('Failed to fetch repayment reminder configurations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const handleOpenModal = (config = null) => {
    setEditingConfig(config);
    if (config) {
      configForm.setFieldsValue({
        trigger_value: config.trigger_value,
        trigger_unit: config.trigger_unit,
        send_email: config.send_email,
        send_sms: config.send_sms,
        is_active: config.is_active
      });
    } else {
      configForm.resetFields();
    }
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    showLoader();
    try {
      const token = localStorage.getItem('loanAdminToken');
      if (token) localStorage.setItem('token', token);

      const res = await httpDelete(`${customBaseUrl.loanUrl}/api/v1/admin/reminder-configs/${id}`, {}, null, false);
      if (res?.status || res?.success) {
        NotificationManager.success(res?.message || 'Reminder configuration deleted successfully.');
        fetchConfigs();
      } else {
        NotificationManager.error(res?.message || 'Failed to delete configuration.');
      }
    } catch (error) {
      console.error('Error deleting config:', error);
      NotificationManager.error('An error occurred while deleting.');
    } finally {
      hideLoader();
    }
  };

  const handleSave = async (values) => {
    setSubmitting(true);
    showLoader();
    try {
      const token = localStorage.getItem('loanAdminToken');
      if (token) localStorage.setItem('token', token);

      if (editingConfig) {
        const res = await httpPut(
          `${customBaseUrl.loanUrl}/api/v1/admin/reminder-configs/${editingConfig.id}`,
          values,
          null,
          false
        );
        if (res?.status || res?.success) {
          NotificationManager.success(res?.message || 'Reminder configuration updated successfully.');
          setModalVisible(false);
          fetchConfigs();
        } else {
          NotificationManager.error(res?.message || 'Failed to update configuration.');
        }
      } else {
        const res = await httpPost(
          `${customBaseUrl.loanUrl}/api/v1/admin/reminder-configs`,
          values,
          false,
          token
        );
        if (res?.status || res?.success) {
          NotificationManager.success(res?.message || 'Reminder configuration created successfully.');
          setModalVisible(false);
          fetchConfigs();
        } else {
          NotificationManager.error(res?.message || 'Failed to create configuration.');
        }
      }
    } catch (error) {
      console.error('Error saving config:', error);
      NotificationManager.error('An error occurred while saving.');
    } finally {
      setSubmitting(false);
      hideLoader();
    }
  };

  const columns = [
    {
      title: 'Trigger Value',
      dataIndex: 'trigger_value',
      key: 'trigger_value',
      render: (val) => <span style={{ fontWeight: 'bold' }}>{val}</span>
    },
    {
      title: 'Trigger Unit',
      dataIndex: 'trigger_unit',
      key: 'trigger_unit',
      render: (unit) => {
        let color = 'blue';
        if (unit === 'hours') color = 'cyan';
        if (unit === 'weeks') color = 'purple';
        return <Tag color={color} style={{ textTransform: 'capitalize', borderRadius: 4 }}>{unit}</Tag>;
      }
    },
    {
      title: 'Channels',
      key: 'channels',
      render: (_, record) => (
        <Space>
          {record.send_email && <Tag color="success" style={{ borderRadius: 4 }}>Email</Tag>}
          {record.send_sms && <Tag color="processing" style={{ borderRadius: 4 }}>SMS</Tag>}
          {!record.send_email && !record.send_sms && <Tag color="default" style={{ borderRadius: 4 }}>None</Tag>}
        </Space>
      )
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active) => (
        <Tag color={active ? 'green' : 'red'} style={{ borderRadius: 4 }}>
          {active ? 'ACTIVE' : 'INACTIVE'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
            size="small"
            style={{ borderRadius: 4 }}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this reminder configuration?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              size="small"
              style={{ borderRadius: 4 }}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontWeight: 600 }}>Reminder Configurations</h3>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleOpenModal()}
          style={{ backgroundColor: '#ff6700', borderColor: '#ff6700', borderRadius: 8, height: 40 }}
        >
          Add Configuration
        </Button>
      </div>

      <Table
        dataSource={configs}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        bordered
      />

      <Modal
        title={editingConfig ? 'Edit Reminder Configuration' : 'Add Reminder Configuration'}
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <Form
          form={configForm}
          layout="vertical"
          onFinish={handleSave}
          initialValues={{
            trigger_value: 1,
            trigger_unit: 'days',
            send_email: true,
            send_sms: true,
            is_active: true
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="trigger_value"
                label="Trigger Value"
                rules={[
                  { required: true, message: 'Please enter a value' },
                  { type: 'number', min: 1, message: 'Minimum value is 1' }
                ]}
              >
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="trigger_unit"
                label="Trigger Unit"
                rules={[{ required: true, message: 'Please select a unit' }]}
              >
                <Select style={{ width: '100%' }}>
                  <Option value="hours">Hours</Option>
                  <Option value="days">Days</Option>
                  <Option value="weeks">Weeks</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={8}>
              <Form.Item name="send_email" valuePropName="checked" label="Send Email">
                <Switch checkedChildren="ON" unCheckedChildren="OFF" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="send_sms" valuePropName="checked" label="Send SMS">
                <Switch checkedChildren="ON" unCheckedChildren="OFF" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="is_active" valuePropName="checked" label="Active">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
            <Button onClick={() => setModalVisible(false)} style={{ borderRadius: 8 }}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              style={{ backgroundColor: '#ff6700', borderColor: '#ff6700', borderRadius: 8 }}
            >
              Save Configuration
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

const LoanSettings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      showLoader();
      try {
        const token = localStorage.getItem('loanAdminToken');
        const res = await httpGet(`${customBaseUrl.loanUrl}/api/v1/admin/loan-settings`, null, false, false, token);
        if (res.status && res.data) {
          form.setFieldsValue(res.data);
        }
      } catch (error) {
        console.error('Error fetching loan settings:', error);
      } finally {
        hideLoader();
      }
    };
    fetchSettings();
  }, [form]);

  const onFinish = async (values) => {
    setLoading(true);
    showLoader();
    try {
      const token = localStorage.getItem('loanAdminToken');
      const res = await httpPost(
        `${customBaseUrl.loanUrl}/api/v1/admin/loan-settings`,
        values,
        false,
        token
      );
      if (res.status) {
        NotificationManager.success('Loan settings saved successfully!');
      } else {
        NotificationManager.error(res.message || 'Failed to save loan settings.');
      }
    } catch (error) {
      console.error('Error saving loan settings:', error);
      NotificationManager.error('An error occurred while saving loan settings.');
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  return (
    <Layout>
      <Card title="Loan Settings">
        <Tabs defaultActiveKey="general" type="line" className="loan-settings-tabs">
          <TabPane tab="General Settings" key="general">
            <div style={{ marginTop: 24 }}>
              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                initialValues={{
                  branch_manager_name: '',
                  branch_manager_title: '',
                  first_repayment_delay_value: 0,
                  first_repayment_delay_unit: 'hours',
                  repayment_grace_period_value: 0,
                  repayment_grace_period_unit: 'hours',
                  repayment_cycle_value: 0,
                  repayment_cycle_unit: 'months'
                }}
              >
                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item
                      name="branch_manager_name"
                      label="Branch Manager Name"
                      rules={[{ required: true, message: 'Please enter the branch manager name.' }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="branch_manager_title"
                      label="Branch Manager Title"
                      rules={[{ required: true, message: 'Please enter the branch manager title.' }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item
                      name="first_repayment_delay_value"
                      label="First Repayment Delay Value"
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <InputNumber style={{ width: '100%' }} min={0} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="first_repayment_delay_unit"
                      label="First Repayment Delay Unit"
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <Select style={{ width: '100%' }}>
                        <Option value="hours">Hours</Option>
                        <Option value="days">Days</Option>
                        <Option value="weeks">Weeks</Option>
                        <Option value="months">Months</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item
                      name="repayment_grace_period_value"
                      label="Repayment Grace Period Value"
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <InputNumber style={{ width: '100%' }} min={0} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="repayment_grace_period_unit"
                      label="Repayment Grace Period Unit"
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <Select style={{ width: '100%' }}>
                        <Option value="hours">Hours</Option>
                        <Option value="days">Days</Option>
                        <Option value="weeks">Weeks</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item
                      name="repayment_cycle_value"
                      label="Repayment Cycle Value"
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <InputNumber style={{ width: '100%' }} min={1} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="repayment_cycle_unit"
                      label="Repayment Cycle Unit"
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <Select style={{ width: '100%' }}>
                        <Option value="hours">Hours</Option>
                        <Option value="days">Days</Option>
                        <Option value="weeks">Weeks</Option>
                        <Option value="months">Months</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={loading} style={{ height: 44, borderRadius: 8 }}>
                    Save Settings
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </TabPane>
          <TabPane tab="Repayment Reminders" key="reminders">
            <ReminderConfigs />
          </TabPane>
        </Tabs>
      </Card>
    </Layout>
  );
};

export default LoanSettings;
